import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import {
  Table,
  Space,
  Input,
  Tag,
  Form,
  Row,
  Col,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  FolderOutlined,
  SearchOutlined,
  StarFilled,
  TagOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { PublicationListOut } from '@/api/generated/schemas';
import { AvailabilityStatus, ModStatus } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { PublicationListCell } from '@/components/user_catalog/publications/PublicationListCell';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { MultiSelectFilter } from '@/components/common/MultiSelectFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { availabilityStatusColors } from './constants';
import { useClassificationNames } from './useClassificationNames';

export const PublicationList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<PublicationListOut>({
      pagination: { pageSize: 20 },
      resource: 'user_catalog.publications',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const rows = (tableProps.dataSource ?? []) as PublicationListOut[];
  const names = useClassificationNames(rows);

  const classificationCounter = (
    icon: ReactNode,
    ids: number[] | undefined,
    lookup: Map<number, string>,
    label: string
  ) => {
    const count = ids?.length ?? 0;
    const content = (
      <Space size={4} style={{ opacity: count ? 1 : 0.35 }}>
        {icon}
        <span>{count}</span>
      </Space>
    );
    if (!count) return content;
    return (
      <Tooltip
        title={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
            {ids!.map((id) => (
              <div key={id}>{lookup.get(id) ?? `#${id}`}</div>
            ))}
          </div>
        }
      >
        {content}
      </Tooltip>
    );
  };

  return (
    <List>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        <Typography.Link
          href="/content_catalog/distribution_periods"
          target="_blank"
          rel="noopener"
        >
          {t('user_catalog.publications.actions.distribution_periods')}{' '}
          <ExportOutlined />
        </Typography.Link>
      </Typography.Paragraph>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('user_catalog.publications.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <ActiveFilters
        filters={filters as CrudFilter[]}
        setFilters={setFilters}
        labelPrefix="user_catalog.publications"
        baseFields={['mod_status', 'updated_at']}
        // This one is shared across user-catalog resources, not per-resource.
        formatField={(field) =>
          field === 'search_popularity'
            ? t('user_catalog.common.fields.search_popularity')
            : undefined
        }
        formatValue={(field, value) =>
          field === 'mod_status'
            ? t(`base.mod_status.${value}`)
            : field === 'availability_status'
              ? t(`user_catalog.publications.availability_status.${value}`)
              : undefined
        }
      />

      {/* Wide table: id + publication stay pinned left and the actions right
          while the middle scrolls. `max-content` sizes columns to their
          content, so pinned ones state a width and `PublicationListCell`
          caps its own text block. */}
      <Table
        {...rowNav('user_catalog.publications', tableProps)}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          fixed="left"
          width={80}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="title"
          title={t('user_catalog.publications.fields.publication')}
          fixed="left"
          sorter
          sortOrder={getSortOrder(sorters, 'title')}
          render={(_, record: PublicationListOut) => (
            <PublicationListCell record={record} />
          )}
        />

        <Table.Column
          // Filterable but not sortable: the backend leaves `publisher_id` out
          // of the sortable set.
          dataIndex="publisher_id"
          title={t('user_catalog.publications.fields.publisher')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              placeholder={t('user_catalog.publications.fields.publisher')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number, record: PublicationListOut) => (
            <ShowIconLink resource="user_catalog.publishers" id={value}>
              {record.publisher?.name ?? `#${value}`}
            </ShowIconLink>
          )}
        />

        <Table.Column
          key="classification"
          title={t('user_catalog.publications.show.sections.classification')}
          render={(_, record: PublicationListOut) => (
            <Space size={12}>
              {classificationCounter(
                <FolderOutlined />,

                record.category_ids,
                names.categories,
                t('user_catalog.publications.fields.categories')
              )}
              {classificationCounter(
                <AppstoreOutlined />,
                record.collection_ids,
                names.collections,
                t('user_catalog.publications.fields.collections')
              )}
              {classificationCounter(
                <TagOutlined />,
                record.custom_tag_ids,
                names.tags,
                t('user_catalog.publications.fields.tags')
              )}
            </Space>
          )}
        />

        <Table.Column
          dataIndex="total_rating"
          title={`${t('user_catalog.publications.fields.total_rating')} / ${t('user_catalog.publications.fields.reviews_count')}`}
          sorter
          sortOrder={getSortOrder(sorters, 'total_rating')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'total_rating')}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="total_rating"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(_: unknown, record: PublicationListOut) => (
            <Space>
              <StarFilled style={{ color: '#fadb14' }} />
              {`${record.total_rating ?? '-'} / ${record.reviews_count ?? '-'}`}
            </Space>
          )}
        />

        {/* Column titles drop the "popularity" word — the group header carries it. */}
        <Table.ColumnGroup
          title={t('user_catalog.publications.fields.popularity')}
        >
          <Table.Column
            dataIndex="popularity"
            title={t('user_catalog.publications.columns.popularity')}
            sorter
            sortOrder={getSortOrder(sorters, 'popularity')}
            render={(v: number) => v ?? 0}
          />

          <Table.Column
            dataIndex="search_popularity"
            title={t('user_catalog.publications.columns.search_popularity')}
            sorter
            sortOrder={getSortOrder(sorters, 'search_popularity')}
            filtered={isFieldFiltered(
              filters as CrudFilter[],
              'search_popularity'
            )}
            filterDropdown={({ confirm, clearFilters }) => (
              <NumberRangeFilter
                field="search_popularity"
                min={0}
                max={10}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
            render={(v: number) => v ?? 0}
          />
        </Table.ColumnGroup>

        {/* Column titles drop the "status" word — the group header carries it. */}
        <Table.ColumnGroup
          title={t('user_catalog.publications.columns.status')}
        >
          <Table.Column
            dataIndex="mod_status"
            title={t('user_catalog.publications.columns.mod_status')}
            render={(value: string) => <ModStatusTag value={value} />}
            filtered={isFieldFiltered(filters as CrudFilter[], 'mod_status')}
            filterDropdown={({ confirm, clearFilters }) => (
              <SelectFilter
                field="mod_status"
                placeholder={t('base.fields.mod_status')}
                options={Object.values(ModStatus).map((value) => ({
                  label: t(`base.mod_status.${value}`),
                  value,
                }))}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
          />

          <Table.Column
            dataIndex="availability_status"
            title={t('user_catalog.publications.columns.availability_status')}
            filtered={isFieldFiltered(
              filters as CrudFilter[],
              'availability_status'
            )}
            filterDropdown={({ confirm, clearFilters }) => (
              <MultiSelectFilter
                field="availability_status"
                placeholder={t(
                  'user_catalog.publications.fields.availability_status'
                )}
                options={Object.values(AvailabilityStatus).map((value) => ({
                  label: t(
                    `user_catalog.publications.availability_status.${value}`
                  ),
                  value,
                }))}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
            render={(value: AvailabilityStatus | null | undefined) =>
              value ? (
                <Tag color={availabilityStatusColors[value] || 'default'}>
                  {t(`user_catalog.publications.availability_status.${value}`)}
                </Tag>
              ) : (
                '-'
              )
            }
          />
        </Table.ColumnGroup>

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          fixed="right"
          width={100}
          render={(_, record: PublicationListOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
